import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { useEffect, useMemo, useState } from 'react'
import { buyerEmailValidationMessage } from '#bv/lib/buyerEmail'
import { tryGetSupabaseClient } from '#bv/lib/supabaseClient'

const searchSchema = z.object({ next: z.string().optional() })

export const Route = createFileRoute('/customer/login')({
  validateSearch: searchSchema,
  component: CustomerLoginPage,
})

function CustomerLoginPage() {
  const navigate = useNavigate()
  const { next } = Route.useSearch()
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [err, setErr] = useState('')
  const [cooldownUntil, setCooldownUntil] = useState<number>(0)
  const [now, setNow] = useState(() => Date.now())

  const cooldownKey = useMemo(() => {
    const e = email.trim().toLowerCase()
    return e ? `bv_magiclink_cooldown_until:${e}` : 'bv_magiclink_cooldown_until:'
  }, [email])

  const cooldownRemainingMs = Math.max(0, cooldownUntil - now)
  const cooldownSeconds = Math.ceil(cooldownRemainingMs / 1000)

  useEffect(() => {
    const run = async () => {
      try {
        const supabase = tryGetSupabaseClient()
        if (!supabase) return
        const { data } = await supabase.auth.getSession()
        if (data.session?.access_token) await navigate({ to: (next?.trim() || '/customer') as never, replace: true })
      } catch {}
    }
    void run()
  }, [navigate, next])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const raw = window.localStorage.getItem(cooldownKey)
    const parsed = raw ? Number(raw) : 0
    setCooldownUntil(Number.isFinite(parsed) ? parsed : 0)
  }, [cooldownKey])

  useEffect(() => {
    if (!cooldownUntil) return
    const id = window.setInterval(() => setNow(Date.now()), 250)
    return () => window.clearInterval(id)
  }, [cooldownUntil])

  const sendLink = async () => {
    setErr('')
    const e = email.trim()
    const emailErr = buyerEmailValidationMessage(e, 'signin')
    if (emailErr) {
      setErr(emailErr)
      return
    }
    if (cooldownUntil && Date.now() < cooldownUntil) { setErr(`Please wait ${Math.ceil((cooldownUntil - Date.now()) / 1000)}s before requesting another email.`); return }
    setBusy(true)
    try {
      const supabase = tryGetSupabaseClient()
      if (!supabase) { setErr('Supabase is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY on Vercel/environment.'); return }
      const redirectTo = `${window.location.origin}/auth/callback?next=${encodeURIComponent(next?.trim() || '/customer')}`
      const { error } = await supabase.auth.signInWithOtp({ email: e, options: { emailRedirectTo: redirectTo } })
      if (error) throw error
      setSent(true)
      const until = Date.now() + 60_000
      setCooldownUntil(until)
      try { window.localStorage.setItem(cooldownKey, String(until)) } catch {}
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Could not send magic link.'
      const lower = msg.toLowerCase()
      if (lower.includes('rate') && lower.includes('limit')) {
        const until = Date.now() + 5 * 60_000
        setCooldownUntil(until)
        try { window.localStorage.setItem(cooldownKey, String(until)) } catch {}
        setErr('Email rate limit reached. Please wait a few minutes, then try again.')
      } else { setErr(msg) }
    } finally { setBusy(false) }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10" style={{ background: '#050508' }}>
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes login-glow{0%,100%{box-shadow:0 0 60px rgba(0, 191, 255,0.06),0 8px 36px rgba(0,0,0,0.45)}50%{box-shadow:0 0 80px rgba(0, 191, 255,0.1),0 8px 36px rgba(0,0,0,0.45)}}
        @keyframes check-in{0%{transform:scale(0) rotate(-45deg);opacity:0}60%{transform:scale(1.2) rotate(0);opacity:1}100%{transform:scale(1) rotate(0);opacity:1}}
      ` }} />

      <div className="w-full max-w-md">
        <div className="rounded-2xl border p-8" style={{ borderColor: 'rgba(0, 191, 255,0.1)', background: 'linear-gradient(165deg, rgba(14,10,18,0.98), rgba(9,7,12,0.96))', animation: 'login-glow 4s ease infinite' }}>
          <div className="mb-7 flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl" style={{ background: 'linear-gradient(135deg, rgba(0, 191, 255,0.15), rgba(139,92,246,0.1))', border: '1px solid rgba(0, 191, 255,0.2)' }}>
              <img
                src="/pulsedigital-logo-mark.png"
                alt=""
                className="h-8 w-auto object-contain"
                style={{ filter: 'drop-shadow(0 0 8px rgba(0, 191, 255,0.35)) drop-shadow(0 0 12px rgba(123, 47, 190,0.25))' }}
              />
            </div>
            <div>
              <h1 className="m-0 font-display text-2xl tracking-tight chrome-text">MY ACCOUNT</h1>
              <p className="m-0 text-xs text-[#5a6d8a]">PulseDigital customer login</p>
            </div>
          </div>

          {sent ? (
            <div className="rounded-xl border border-emerald-500/20 p-5 text-center" style={{ background: 'rgba(16,185,129,0.04)' }}>
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full" style={{ background: 'rgba(16,185,129,0.12)' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2.5" style={{ animation: 'check-in 0.5s ease forwards' }}><polyline points="20 6 9 17 4 12" /></svg>
              </div>
              <p className="m-0 mt-4 text-sm font-semibold text-emerald-200">Magic link sent</p>
              <p className="m-0 mt-1.5 text-xs text-emerald-100/70">Check your inbox and click the sign-in link to finish logging in.</p>
              <button type="button" className="mt-4 text-xs font-semibold text-[#93c5fd] hover:underline" onClick={() => setSent(false)}>
                Send another link
              </button>
            </div>
          ) : (
            <>
              <label className="block">
                <span className="mb-1.5 block text-[0.6rem] font-bold uppercase tracking-widest text-[#5a6d8a]">Email</span>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5a6d8a" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  </div>
                  <input
                    id="customer-login-email"
                    name="email"
                    type="text"
                    inputMode="email"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    className="bv-input w-full rounded-xl py-3 pl-11 pr-4 text-sm"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void sendLink()
                    }}
                  />
                </div>
              </label>

              {err ? (
                <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-500/15 px-3 py-2" style={{ background: 'rgba(239,68,68,0.04)' }}>
                  <svg className="mt-0.5 shrink-0" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <p className="m-0 text-xs text-red-300">{err}</p>
                </div>
              ) : null}

              <button type="button" onClick={() => void sendLink()} disabled={busy || cooldownSeconds > 0} className="btn-primary mt-5 w-full rounded-xl py-3.5 text-sm font-semibold disabled:opacity-50">
                {busy ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent" style={{ animation: 'spin 0.8s linear infinite' }} />
                    Sending...
                  </span>
                ) : cooldownSeconds > 0 ? `Try again in ${cooldownSeconds}s` : 'Send magic link'}
              </button>

              <div className="mt-4 flex items-center gap-2 text-[0.65rem] text-[#5a6d8a]">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                <span>Secure passwordless login via magic link</span>
              </div>
            </>
          )}
        </div>

        <p className="mt-5 text-center text-xs text-[#5a6d8a]">
          <Link to="/" className="inline-flex items-center gap-1 text-[#5a6d8a] transition-colors hover:text-[#8ecfff]">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
            Back to store
          </Link>
        </p>
      </div>

      <style dangerouslySetInnerHTML={{ __html: '@keyframes spin{to{transform:rotate(360deg)}}' }} />
    </div>
  )
}
